import { useAudioPlayer } from 'expo-audio';
import { ComponentProps } from 'react';
import {
  Easing,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import CorrectAudio from '../../assets/CorrectAudio.mp3';
import IncorrectAudio from '../../assets/IncorrectAudio.mp3';
import { catchChromeAutoplayError } from '../../lib/catchChromeAutoplayError';
import { useTheme } from '../../styles';
import { Button } from '../Button';

/**
 * A button that gives feedback when pressed.
 */
export const CorrectIncorrectButton = ({
  text,
  onPress,
  correct,
}: {
  text: string;
  correct: boolean;
  onPress: ComponentProps<typeof Button>['onPress'];
}) => {
  const { theme } = useTheme();
  const colorProgress = useSharedValue(0);
  const shakeProgress = useSharedValue(0);
  const audioPlayer = useAudioPlayer(correct ? CorrectAudio : IncorrectAudio);

  const style = useAnimatedStyle(() => {
    const color = interpolateColor(
      colorProgress.get(),
      [0, 1],
      [theme.color.primary100, correct ? theme.color.success : theme.color.danger],
    );

    const translate = interpolate(shakeProgress.get(), [0, 1 / 3, 2 / 3, 1], [0, 10, -10, 0]);

    return {
      borderColor: color,
      backgroundColor: color,
      transform: [correct ? { translateY: translate } : { translateX: translate }],
    };
  }, [colorProgress, correct]);

  return (
    <Button
      _useAnimated
      testID={correct ? 'CorrectIncorrectButton_correct' : 'CorrectIncorrectButton_incorrect'}
      style={style}
      text={text}
      onPress={() => {
        const animationDelay = 300;
        setTimeout(() => {
          void catchChromeAutoplayError(() => audioPlayer.play());
        }, animationDelay);
        colorProgress.set(withTiming(1));
        shakeProgress.set(
          withDelay(
            animationDelay,
            withRepeat(withTiming(1, { easing: Easing.inOut(Easing.linear) }), 3, false),
          ),
        );
        return onPress?.();
      }}
      variant="solid"
      textStyle={{ textAlign: 'center' }}
    />
  );
};
